import type { ProjectData } from "grapesjs";
import type { PageTypes } from "@/App";
import { upgradeStandardButtons } from "./standardTemplateButtons";

type TemplateUrls = { config_draft_url?: string; config_published_url?: string };

export type RemoteStorageDeps<T extends TemplateUrls> = {
  type: PageTypes;
  /** Read on every load/store call, never captured: the rename modal changes the name mid-session. */
  getTemplate: () => T | undefined;
  /** Fallback when the template list has not loaded yet. */
  loadTemplate: () => Promise<T | undefined>;
  fetchTemplate: (url: string) => Promise<ProjectData>;
  saveTemplate: (
    formData: FormData,
    template: T & { type: PageTypes; explicit?: boolean }
  ) => Promise<unknown>;
};

export function createRemoteStorage<T extends TemplateUrls>(deps: RemoteStorageDeps<T>) {
  return {
    async load() {
      const template = deps.getTemplate();
      return upgradeStandardButtons(
        await deps.fetchTemplate(
          template?.config_draft_url || template?.config_published_url || ""
        )
      );
    },
    async store(data: ProjectData, opts: { explicit?: boolean }) {
      const formData = data as unknown as FormData;
      const template = deps.getTemplate();
      if (template) {
        // Return the promise so GrapesJS Storage.store() can await the save
        // and surface failures, instead of fire-and-forget.
        return deps.saveTemplate(formData, {
          ...template,
          type: deps.type,
          explicit: opts.explicit,
        });
      }
      const loaded = await deps.loadTemplate();
      return deps.saveTemplate(formData, { ...(loaded as T), type: deps.type });
    },
  };
}
